import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';

import YRTable from 'components/year-review/components/table/YRTable';
import YRTableMap from 'components/year-review/components/table/YRTableMap';
import YRToggle from 'components/year-review/components/toggle/YRToggle';

const FallBack = () => (
    <div />
);

const YRGraph = ({ data }) => {
    const chartContainerRef = useRef(null);

    const [graphReady, setGraphReady] = useState(false);
    const [showGraph, setShowGraph] = useState(true);

    const { type, style, name } = data;
    const hasTableToggle = type !== 'LIST' && type !== 'STATS' && type !== 'TABLE' && type !== 'MULTIPIE';
    let Graph;

    useEffect(() => {
        let io = null;

        if (!!window.IntersectionObserver) {
            io = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setGraphReady(true);
                        io.disconnect();
                    }
                });
            });

            io.observe(chartContainerRef.current);
        } else {
            setGraphReady(true);
        }

        return () => {
            io.disconnect();
        };
    }, [chartContainerRef]);

    switch (type) {
        case 'BARCHART':
            Graph = lazy(() => import('./barchart/YRBarChart'));
            break;
        case 'STACKED-BARCHART':
            Graph = lazy(() => import('./barchart/YRStackedBarChart'));
            break;
        case 'LINECHART':
            Graph = lazy(() => import('./linechart/YRLineChart'));
            break;
        case 'PIECHART':
            Graph = lazy(() => import('./piechart/YRPieChart'));
            break;
        case 'LIST':
            Graph = lazy(() => import('./list/YRList'));
            break;
        case 'STATS':
            Graph = lazy(() => import('./stats/YRStats'));
            break;
        case 'TABLE':
            Graph = lazy(() => import('./table/YRBigTable'));
            break;
        case 'MULTIPIE':
            Graph = lazy(() => import('./piechart/YRMultiPie'));
            break;
        case 'MAP':
            Graph = lazy(() => import('./map/YRMap'));
            break;
        default:
    }

    return (
        <div
            className={`chart-container${graphReady ? ' is-ready' : ''}`}
            ref={chartContainerRef}
        >
            <div className="chart-container__block">
                <div
                    role="tabpanel"
                    tabIndex="0"
                    className="chart-container__tab"
                    hidden={!showGraph}
                >
                    <Suspense fallback={<FallBack />}>
                        <Graph
                            data={data.values}
                            style={style}
                            isReady={graphReady}
                        />
                    </Suspense>
                </div>
                <div
                    role="tabpanel"
                    tabIndex="0"
                    className="chart-container__tab"
                    hidden={showGraph}
                >
                    {(type && type === 'MAP') ? (
                        <YRTableMap
                            data={data.values}
                            type={type}
                            style={style}
                            name={name}
                        />
                    ) : (
                        <YRTable
                            data={data.values}
                            type={type}
                            style={style}
                            name={name}
                        />
                    )}
                </div>

                {hasTableToggle && (
                    <YRToggle
                        showGraph={showGraph}
                        onClick={(showGraphValue) => {
                            setShowGraph(showGraphValue);
                            chartContainerRef.current.focus();
                        }}
                    />
                )}
            </div>
        </div>
    );
};

YRGraph.propTypes = {
    data: PropTypes.objectOf(PropTypes.any).isRequired,
};


export default YRGraph;
